import Phaser from 'phaser'

/**
 * Visual and audio feedback utilities
 */

export interface ScorePopupConfig {
  x: number
  y: number
  points: number
  color?: number
  fontSize?: number
}

/**
 * Show floating score popup that animates up and fades out
 */
export function showScorePopup(
  scene: Phaser.Scene,
  config: ScorePopupConfig
): Phaser.GameObjects.Text {
  const { x, y, points, color = 0x4caf50, fontSize = 32 } = config

  const prefix = points >= 0 ? '+' : ''
  const textColor = points >= 0 ? `#${color.toString(16).padStart(6, '0')}` : '#ff4444'

  const text = scene.add.text(x, y, `${prefix}${points}`, {
    fontFamily: 'system-ui',
    fontSize: `${fontSize}px`,
    fontStyle: 'bold',
    color: textColor,
    stroke: '#ffffff',
    strokeThickness: 4,
  })
  text.setOrigin(0.5)
  text.setDepth(1000)

  scene.tweens.add({
    targets: text,
    y: y - 80,
    alpha: 0,
    scale: 1.2,
    duration: 800,
    ease: 'Quad.easeOut',
    onComplete: () => text.destroy(),
  })

  return text
}

/**
 * Shake the camera for impact feedback
 */
export function shakeScreen(
  scene: Phaser.Scene,
  intensity: number = 0.005,
  duration: number = 100
): void {
  scene.cameras.main.shake(duration, intensity)
}

/**
 * Flash the screen briefly
 */
export function flashScreen(
  scene: Phaser.Scene,
  color: number = 0xffffff,
  duration: number = 100
): void {
  scene.cameras.main.flash(duration, (color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff)
}

/**
 * Bounce animation for game objects
 */
export function bounceObject(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  scale: number = 1.2
): void {
  scene.tweens.add({
    targets: target,
    scale: scale,
    duration: 100,
    yoyo: true,
    ease: 'Quad.easeOut',
  })
}

/**
 * Wobble animation for game objects
 */
export function wobbleObject(
  scene: Phaser.Scene,
  target: Phaser.GameObjects.GameObject,
  angle: number = 5,
  repeats: number = 3
): void {
  scene.tweens.add({
    targets: target,
    angle: angle,
    duration: 50,
    yoyo: true,
    repeat: repeats,
    ease: 'Sine.easeInOut',
  })
}

/**
 * Show "Perfect!", "Great!", etc. feedback text
 */
export function showFeedbackText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  color: number = 0xffd700
): Phaser.GameObjects.Text {
  const feedbackText = scene.add.text(x, y, text, {
    fontFamily: 'system-ui',
    fontSize: '48px',
    fontStyle: 'bold',
    color: `#${color.toString(16).padStart(6, '0')}`,
    stroke: '#ffffff',
    strokeThickness: 6,
  })
  feedbackText.setOrigin(0.5)
  feedbackText.setDepth(1000)
  feedbackText.setScale(0)

  scene.tweens.add({
    targets: feedbackText,
    scale: 1,
    duration: 200,
    ease: 'Back.easeOut',
    onComplete: () => {
      scene.tweens.add({
        targets: feedbackText,
        alpha: 0,
        y: y - 50,
        duration: 500,
        delay: 300,
        onComplete: () => feedbackText.destroy(),
      })
    },
  })

  return feedbackText
}

/**
 * Create particle burst effect
 */
export function createParticleBurst(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: number = 0xffd700,
  count: number = 10
): void {
  // Create temporary graphics for particle texture
  const graphics = scene.make.graphics({ x: 0, y: 0 })
  graphics.fillStyle(color)
  graphics.fillCircle(8, 8, 8)
  graphics.generateTexture('particle_burst', 16, 16)
  graphics.destroy()

  const particles = scene.add.particles(x, y, 'particle_burst', {
    speed: { min: 100, max: 200 },
    scale: { start: 0.5, end: 0 },
    lifespan: 500,
    quantity: count,
    emitting: false,
  })

  particles.explode()

  scene.time.delayedCall(600, () => particles.destroy())
}
